/**
 * @graphql cc
 */
import { useState, useContext, useEffect } from 'react';
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import {
	useIsLivePagesFeatureEnabled,
	LIVE_DOC_GA_EXP_TRAIT_NAME,
	LIVE_DOC_GA_EXP_CONTROL_GROUP_VALUE,
} from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { fg } from '@confluence/feature-gating';

import type {
	InitModernizationGaMutation as InitModernizationGaMutationType,
	InitModernizationGaMutationVariables,
} from './__types__/InitModernizationGaMutation';

const InitModernizationGaMutation = gql`
	mutation InitModernizationGaMutation($cloudId: ID!) {
		confluence_experimentInitModernize(cloudId: $cloudId) {
			success
		}
	}
`;

/**
 * This component is used to initialize the Modernization GA experiment.
 * It does not render anything.
 */
export const InitModernizationGa = () => {
	const [isInitialized, setIsInitialized] = useState(false);

	const { cloudId } = useSessionData();
	const [initModernizationGaMutation] = useMutation<
		InitModernizationGaMutationType,
		InitModernizationGaMutationVariables
	>(InitModernizationGaMutation, {
		variables: { cloudId },
	});

	// Two keys so that those who were initialized during the control group of the 50/50 experiment
	// are not blocked from initializing modernization ga post experiment should they now be eligible
	const hasInitializedModernizationGa = localStorage.getItem(
		fg('modernize_ga_exp_post_rollout')
			? keys.LIVE_DOCS_HAS_INIT_MODERNIZE_POST_EXP
			: keys.LIVE_DOCS_HAS_INIT_MODERNIZE,
	);
	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();
	const { experimentFeatures, loading: isSPAViewContextLoading } = useContext(SPAViewContext);

	useEffect(() => {
		if (
			isInitialized ||
			hasInitializedModernizationGa ||
			isLivePagesFeatureEnabled ||
			isSPAViewContextLoading ||
			!experimentFeatures
		) {
			return;
		}
		try {
			const modernizationGaTraitValue =
				JSON.parse(experimentFeatures)?.[LIVE_DOC_GA_EXP_TRAIT_NAME];

			if (
				// modernization trait is not set
				modernizationGaTraitValue === 'null' ||
				!modernizationGaTraitValue ||
				// modernization trait is set to control and the post exp rollout fg is on
				// this allows tenants who were in the control group during the experiment to now have access to modernization features post 50/50 experiment
				(modernizationGaTraitValue === LIVE_DOC_GA_EXP_CONTROL_GROUP_VALUE &&
					fg('modernize_ga_exp_post_rollout'))
			) {
				setIsInitialized(true);
				void initModernizationGaMutation().then(({ data }) => {
					data?.confluence_experimentInitModernize?.success &&
						localStorage.setItem(
							fg('modernize_ga_exp_post_rollout')
								? keys.LIVE_DOCS_HAS_INIT_MODERNIZE_POST_EXP
								: keys.LIVE_DOCS_HAS_INIT_MODERNIZE,
							'true',
						);
				});
			}
		} catch (e) {
			// do nothing
		}
	}, [
		isInitialized,
		hasInitializedModernizationGa,
		isLivePagesFeatureEnabled,
		isSPAViewContextLoading,
		experimentFeatures,
		initModernizationGaMutation,
	]);

	return null;
};
